import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Hero from '../components/Hero';
import { CheckIcon, StarIcon } from '../components/Icons';
import { getTestimonials } from '../services/api';
import './DevHome.css';

// Services offered
const devServices = [
  {
    id: 'web-apps',
    title: 'Web Applications',
    description: 'Full-stack web applications built with modern frameworks. React, Node.js, and cloud infrastructure.',
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
        <rect x="2" y="3" width="20" height="14" rx="2" ry="2"></rect>
        <line x1="8" y1="21" x2="16" y2="21"></line>
        <line x1="12" y1="17" x2="12" y2="21"></line>
      </svg>
    ),
    features: ['React & Next.js', 'Node.js Backend', 'PostgreSQL/MongoDB', 'AWS/Cloud Hosting']
  },
  {
    id: 'mobile-apps',
    title: 'Mobile Apps',
    description: 'Native and cross-platform mobile applications for iOS and Android with seamless user experiences.',
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
        <rect x="5" y="2" width="14" height="20" rx="2" ry="2"></rect>
        <line x1="12" y1="18" x2="12" y2="18"></line>
      </svg>
    ),
    features: ['React Native', 'iOS & Android', 'Push Notifications', 'Offline Support']
  },
  {
    id: 'crm-portals',
    title: 'CRMs & Portals',
    description: 'Custom business management systems tailored to your workflow. Client portals, dashboards, and admin tools.',
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
        <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path>
        <circle cx="9" cy="7" r="4"></circle>
        <path d="M23 21v-2a4 4 0 0 0-3-3.87"></path>
        <path d="M16 3.13a4 4 0 0 1 0 7.75"></path>
      </svg>
    ),
    features: ['Client Management', 'Invoicing Systems', 'Team Collaboration', 'Analytics Dashboard']
  },
  {
    id: 'custom-ui',
    title: 'Custom UI/UX',
    description: 'Beautiful, intuitive interfaces designed for your brand. From wireframes to polished production designs.',
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
        <polygon points="12 2 2 7 12 12 22 7 12 2"></polygon>
        <polyline points="2 17 12 22 22 17"></polyline>
        <polyline points="2 12 12 17 22 12"></polyline>
      </svg>
    ),
    features: ['UI/UX Design', 'Design Systems', 'Prototyping', 'Brand Integration']
  }
];

// Featured projects - Using actual client hero images
const featuredProjects = [
  {
    id: 'armory360',
    title: 'Armory360',
    client: 'Armory360',
    description: 'Privacy-first iPhone and iPad app for firearm owners. Tracks firearms, ammo, range days, drills, maintenance and documents — all encrypted on-device with no account, no tracking, and optional encrypted iCloud sync.',
    tech: ['iOS', 'iPadOS', 'Swift', 'On-Device Encryption', 'iCloud Sync'],
    image: 'https://armory360.app/og.jpg',
    metrics: ['iPhone & iPad', 'Privacy-First', 'English & Spanish'],
    url: 'https://armory360.app'
  },
  {
    id: 'sage-southern-cuisine',
    title: 'Sage Southern Cuisine',
    client: 'Sage Southern Cuisine',
    description: 'Restaurant website for a scratch-made Southern comfort food kitchen in West Nyack, NY, with full menu, online ordering for pickup, and a warm brand-driven design.',
    tech: ['React', 'Online Ordering', 'Responsive Design', 'SEO'],
    image: 'https://www.sagesoutherncuisine.com/images/hero-home.jpg',
    metrics: ['Online Ordering', 'Mobile Ready', 'West Nyack, NY'],
    url: 'https://www.sagesoutherncuisine.com'
  },
  {
    id: 'ccb-website',
    title: 'CCB NYC Website & App',
    client: 'CCB NYC Church',
    description: 'Church website and mobile app featuring service times, event calendar, sermon archives, ministry information, and community engagement tools.',
    tech: ['React', 'React Native', 'Node.js', 'PostgreSQL'],
    image: 'https://www.ccb-nyc.com/images/home-hero.jpg',
    metrics: ['Growing Community', 'Mobile Ready', '4.7★ App Rating'],
    url: 'https://www.ccb-nyc.com'
  },
  {
    id: 'gvc-transportation',
    title: 'GVC Website, Portal & CMS',
    client: 'GVC Transportation',
    description: 'Website and operations portal for a school bus transportation company serving the Bronx. Public site covers services, routes and safety; the portal manages routes, schedules, driver assignments and parent communications.',
    tech: ['React', 'Vite', 'Node.js', 'PostgreSQL', 'AWS S3'],
    image: '/images/portfolio/gvc-hero.jpg',
    metrics: ['Route Management', 'Driver Portal', 'Admin Dashboard'],
    url: 'https://gvcbus.com'
  },
  {
    id: 'svt-portal',
    title: 'SVT Business Portal',
    client: 'SVT Consulting',
    description: 'Internal business management platform handling clients, service calls, invoicing, HR management, and team communication.',
    tech: ['React', 'Node.js', 'AWS S3', 'PostgreSQL'],
    image: 'https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=600&h=400&fit=crop',
    metrics: ['50% Time Saved', 'Real-time Updates', 'iOS App']
  },
  {
    id: 'hoadesk',
    title: 'HOAdesk',
    client: 'HOAdesk',
    description: 'AI-powered HOA management SaaS for small communities. Records board meetings and turns them into structured minutes, with resident payments, a resident mobile app, compliance monitoring and violation tracking.',
    tech: ['Next.js', 'React Native', 'Supabase', 'Stripe', 'AI/LLM'],
    image: '/images/portfolio/hoadesk-hero.jpg',
    metrics: ['AI Minutes', 'Multi-Tenant SaaS', 'iOS & Android'],
    url: 'https://hoadesk.io'
  }
];

// Tech stack
const techStack = [
  { name: 'React', category: 'Frontend' },
  { name: 'React Native', category: 'Mobile' },
  { name: 'Swift', category: 'Mobile' },
  { name: 'Node.js', category: 'Backend' },
  { name: 'PostgreSQL', category: 'Database' },
  { name: 'MongoDB', category: 'Database' },
  { name: 'AWS', category: 'Cloud' },
  { name: 'TypeScript', category: 'Language' },
  { name: 'Python', category: 'Language' },
  { name: 'Docker', category: 'DevOps' },
  { name: 'Git', category: 'DevOps' }
];

const DevHome = () => {
  const [testimonials, setTestimonials] = useState([]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const testimonialsData = await getTestimonials({ featured: 'true', limit: 3 });
        setTestimonials(testimonialsData);
      } catch (error) {
        console.error('Error fetching testimonials:', error);
      }
    };
    fetchData();
  }, []);

  const renderStars = (rating) => {
    return Array.from({ length: 5 }, (_, i) => (
      <StarIcon key={i} size={18} filled={i < rating} color="#FF5722" />
    ));
  };

  return (
    <div className="dev-home-page">
      <Hero
        title={<>Build Your Digital <span>Future</span></>}
        subtitle="Custom web applications, mobile apps, CRMs, and software solutions tailored to your business needs."
        backgroundImage="https://plus.unsplash.com/premium_photo-1663023612721-e588768ef403?q=80&w=2342&auto=format&fit=crop"
        ctaText="Start Your Project"
        ctaLink="/contact"
        secondaryCtaText="View Our Work"
        secondaryCtaLink="/portfolio"
        overlay={0.75}
      />

      {/* Services Section */}
      <section className="section dev-services-section">
        <div className="container">
          <div className="section-title">
            <h2>What We Build</h2>
            <p>End-to-end digital solutions from concept to deployment</p>
          </div>
          <div className="dev-services-grid">
            {devServices.map((service) => (
              <div className="dev-service-card" key={service.id}>
                <div className="dev-service-icon">
                  {service.icon}
                </div>
                <h3>{service.title}</h3>
                <p>{service.description}</p>
                <ul className="dev-service-features">
                  {service.features.map((feature, i) => (
                    <li key={i}>
                      <CheckIcon size={16} color="#FF5722" />
                      {feature}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Featured Projects */}
      <section className="section section-dark dev-projects-section">
        <div className="container">
          <div className="section-title">
            <h2>Featured Projects</h2>
            <p>Real solutions we've built for real businesses</p>
          </div>
          <div className="dev-projects-grid">
            {featuredProjects.map((project) => (
              <div className="dev-project-card" key={project.id}>
                <div 
                  className="dev-project-image"
                  style={{ backgroundImage: `url(${project.image})` }}
                >
                  <div className="dev-project-overlay">
                    <div className="dev-project-metrics">
                      {project.metrics.map((metric, i) => (
                        <span key={i} className="metric-badge">{metric}</span>
                      ))}
                    </div>
                  </div>
                </div>
                <div className="dev-project-content">
                  <span className="dev-project-client">{project.client}</span>
                  <h3>{project.title}</h3>
                  <p>{project.description}</p>
                  <div className="dev-project-tech">
                    {project.tech.map((t, i) => (
                      <span key={i} className="tech-tag">{t}</span>
                    ))}
                  </div>
                  {project.url && (
                    <a
                      href={project.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="btn btn-outline btn-sm"
                      style={{ marginTop: '1rem' }}
                    >
                      Visit Site →
                    </a>
                  )}
                </div>
              </div>
            ))}
          </div>
          <div className="section-cta">
            <Link to="/portfolio" className="btn btn-primary">View All Projects</Link>
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section className="section dev-process-section">
        <div className="container">
          <div className="section-title">
            <h2>Our Process</h2>
            <p>From idea to launch in four streamlined phases</p>
          </div>
          <div className="process-timeline">
            <div className="process-step">
              <div className="step-number">01</div>
              <h3>Discovery</h3>
              <p>We dive deep into your business needs, goals, and challenges to create a comprehensive project roadmap.</p>
            </div>
            <div className="process-step">
              <div className="step-number">02</div>
              <h3>Design</h3>
              <p>Our team creates wireframes and prototypes, refining the user experience until it's pixel-perfect.</p>
            </div>
            <div className="process-step">
              <div className="step-number">03</div>
              <h3>Develop</h3>
              <p>Using agile methodology, we build your solution with regular updates and continuous feedback loops.</p>
            </div>
            <div className="process-step">
              <div className="step-number">04</div>
              <h3>Deploy</h3>
              <p>We launch your product, provide training, and offer ongoing support to ensure long-term success.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Tech Stack */}
      <section className="section section-gray tech-stack-section">
        <div className="container">
          <div className="section-title">
            <h2>Our Tech Stack</h2>
            <p>Modern technologies for scalable, maintainable solutions</p>
          </div>
          <div className="tech-stack-grid">
            {techStack.map((tech, i) => (
              <div className="tech-item" key={i}>
                <span className="tech-name">{tech.name}</span>
                <span className="tech-category">{tech.category}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      {testimonials.length > 0 && (
        <section className="section testimonials-section">
          <div className="container">
            <div className="section-title">
              <h2>Client Success Stories</h2>
              <p>Hear from businesses we've helped transform</p>
            </div>
            <div className="testimonials-grid">
              {testimonials.map(testimonial => (
                <div className="testimonial-card" key={testimonial.id}>
                  <div className="testimonial-rating">
                    {renderStars(testimonial.rating || 5)}
                  </div>
                  <p className="testimonial-text">"{testimonial.testimonial_text}"</p>
                  <div className="testimonial-author">
                    {testimonial.client_photo_url && (
                      <img src={testimonial.client_photo_url} alt={testimonial.client_name} />
                    )}
                    <div>
                      <strong>{testimonial.client_name}</strong>
                      {testimonial.client_title && (
                        <span>{testimonial.client_title}{testimonial.client_company && `, ${testimonial.client_company}`}</span>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* CTA Section */}
      <section className="cta-section dev-cta">
        <div className="container">
          <h2>Ready to Build Something Great?</h2>
          <p>Let's discuss your project and create a custom solution for your business</p>
          <div className="cta-buttons">
            <Link to="/contact" className="btn btn-primary">Start Your Project</Link>
            <a href="tel:+18453043934" className="btn btn-outline">Schedule a Call</a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default DevHome;
